/**
 * @file inward-approval.ts
 * @module app/modules/inward/utils/inward-approval
 * @description Pure helpers for the Inward GRN approval screens: approval status, file
 *   rules (same as the server), and the request bodies. Two approvals run in parallel:
 *   Warehouse (photo proof) and Finance (documents; stock goes live).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

export const APPROVAL_PENDING = 1;
export const APPROVAL_APPROVED = 2;
/** the whole GRN was rejected by Warehouse or Finance */
export const APPROVAL_REJECTED = 3;

/** Missing / damaged on a GRN line: Warehouse or Finance approves or rejects it. */
export const SHORTFALL_PENDING = 1;
export const SHORTFALL_APPROVED = 2;
export const SHORTFALL_REJECTED = 3;

export type ApprovalKind = "warehouse" | "finance";
export type GrnQueue = "warehouse" | "finance" | "completed" | "";

export const GRN_QUEUES: { label: string; value: GrnQueue }[] = [
  { label: "Waiting for Warehouse", value: "warehouse" },
  { label: "Waiting for Finance", value: "finance" },
  { label: "All stock live", value: "completed" },
  { label: "All", value: "" },
];

export interface InwardPersonRef {
  reference?: string;
  name?: string;
}

export interface ApprovalState {
  status?: number;
  by?: string;
  byName?: string;
  at?: string;
  images?: string[];
  documents?: string[];
}

/** One row of the Received goods table (computed by the API). */
export interface ReceivedLine {
  key: string;
  variant: string;
  title: string;
  type: number;
  unit: string;
  unitShort: string;
  poQty: number;
  received: number;
  remaining: number;
  missing: number;
  damaged: number;
  /** PO selling price */
  sellingPrice: number;
  /** Price Catalog selling price (active, this warehouse); null when missing */
  newSellingPrice: number | null;
  costPrice: number;
  poCostPrice: number;
  gpPercent: number | null;
  taxPercent: number;
  amount: number;
  taxAmount: number;
  totalAmount: number;
  financeApproved: boolean;
  financeAt?: string;
  financeBy?: string;
  stockLive: boolean;
  costChange?: {
    status: number;
    poCost: number;
    invoiceCost: number;
    alertId: string;
  };
  /** cost above the catalog selling price */
  negativeGp?: boolean;
  /** Finance approved it with the cost above the selling price: out of stock until
   *  procurement updates the selling price */
  priceBlocked?: boolean;
  /** latest selling price change made by procurement (2 = applied) */
  sellingChange?: {
    id: string;
    status: number;
    oldSellingPrice: number | null;
    newSellingPrice: number;
    note: string;
    proposedBy?: string;
    proposedAt?: string;
    decidedBy?: string;
    decidedAt?: string;
    decisionNote?: string;
  };
}

export interface ReceivedTotals {
  received: number;
  missing: number;
  damaged: number;
  remaining: number;
  amount: number;
  taxAmount: number;
  totalAmount: number;
  skuCount: number;
  lineCount: number;
  financeApprovedLines: number;
  liveLines: number;
  priceBlockedLines?: number;
}

export interface FinanceSubmission {
  lines?: { variant: string; type: number }[];
  invoiceNumber?: string;
  receiptNumber?: string;
  receiptVerificationCode?: string;
  documents?: string[];
  by?: InwardPersonRef;
  at?: string;
}

export const COST_CHANGE_PENDING = 1;

/** GP % for a selling price and cost (null when there is no price). */
export const gpPercent = (
  selling: number | null | undefined,
  cost: number
): number | null =>
  selling && selling > 0
    ? Math.round(((selling - cost) / selling) * 10000) / 100
    : null;

/**
 * Procurement's new selling price: required, same as or above the cost price, and a note.
 * Returns an error message or null.
 */
export const checkSellingProposal = (
  price: string,
  note: string,
  cost: number
): string | null => {
  const n = Number(String(price ?? "").trim());
  if (String(price ?? "").trim() === "" || !Number.isFinite(n) || n <= 0) {
    return "Enter the new selling price";
  }
  if (n < cost)
    return `The selling price must be the same as or higher than the cost price (${cost})`;
  if (!String(note ?? "").trim())
    return "Add a note explaining the selling price change";
  return null;
};

/**
 * Held back (cost above selling price) and waiting for procurement's selling price. When a
 * cost change is still to be acknowledged, the price is entered in that step instead.
 */
export const needsSellingUpdate = (l: ReceivedLine) =>
  !!l.priceBlocked &&
  !l.stockLive &&
  l.costChange?.status !== COST_CHANGE_PENDING;

/** A pending cost change that needs a new selling price with it (cost above selling price). */
export const costNeedsSelling = (l: ReceivedLine) =>
  l.costChange?.status === COST_CHANGE_PENDING &&
  !l.stockLive &&
  (!!l.priceBlocked ||
    (l.newSellingPrice !== null &&
      l.costChange.invoiceCost > (l.newSellingPrice as number)));

/** Finance-approved products only waiting for stock to be credited (not held back). */
export const waitingForStock = (grn: InwardGrnRecord) =>
  (grn.receivedLines ?? []).filter(
    (l) => l.financeApproved && !l.stockLive && !l.priceBlocked
  );

export interface InwardGrnRecord {
  _id: string;
  refKey: string;
  poRefKey?: string;
  date?: string;
  _createdAt?: string;
  businessDetails?: { ownerName?: string; name?: string };
  warehouse?: { name?: string };
  driverName?: string;
  driverPhone?: string;
  vehicleNumber?: string;
  gateEntryNumber?: string;
  receiptDate?: string;
  invoiceNumber?: string;
  receiptNumber?: string;
  receiptVerificationCode?: string;
  variants?: {
    variant: string;
    title?: string;
    quantityTypes?: {
      type: number;
      stockCount?: number;
      rejectedStockCount?: number;
      reason?: number;
    }[];
    batches?: {
      batch: number;
      expiry?: string;
      quantityTypes?: { type: number; stockCount?: number }[];
    }[];
  }[];
  inward?: {
    loadingBayManager?: InwardPersonRef;
    dock?: InwardPersonRef;
    startedAt?: string;
    startedBy?: InwardPersonRef;
    doneAt?: string;
    doneBy?: InwardPersonRef;
    stockLiveAt?: string;
    actions?: {
      action: string;
      by?: InwardPersonRef;
      at?: string;
      note?: string;
    }[];
    warehouseApproval?: ApprovalState;
    financeApproval?: ApprovalState;
    financeSubmissions?: FinanceSubmission[];
    lines?: {
      variant: string;
      type: number;
      financeStatus?: number;
      stockLiveAt?: string;
      missing?: number;
      damaged?: number;
      shortfallStatus?: number;
      shortfallBy?: InwardPersonRef;
      shortfallAt?: string;
      shortfallStage?: string;
      shortfallNote?: string;
    }[];
    /** the whole GRN was rejected: its quantities went back to the PO */
    rejection?: {
      by?: InwardPersonRef;
      at?: string;
      stage?: string;
      reason?: string;
    };
    costChanges?: {
      alertId: string;
      variant: string;
      type: number;
      poCost: number;
      invoiceCost: number;
      status: number;
    }[];
  };
  receivedLines?: ReceivedLine[];
  totals?: ReceivedTotals;
  /** received lines (list API) */
  receivedLineCount?: number;
  /** set when Finance was saved but stock could not be credited yet */
  stockError?: string;
  catalogPrices?: {
    variant: string;
    type: number;
    sellingPrice: number | null;
  }[];
}

export const isApproved = (a?: ApprovalState) =>
  a?.status === APPROVAL_APPROVED;

export const approvalLabel = (a?: ApprovalState) =>
  isApproved(a)
    ? "Approved"
    : a?.status === APPROVAL_REJECTED
    ? "Rejected"
    : "Pending";

export const isGrnRejected = (grn?: InwardGrnRecord | null) =>
  !!grn?.inward?.rejection;

/** A GRN can be rejected while nothing of it is live in stock (and it is not rejected yet). */
export const canRejectGrn = (grn?: InwardGrnRecord | null) =>
  !!grn &&
  !isGrnRejected(grn) &&
  !grn.inward?.stockLiveAt &&
  !(grn.inward?.lines ?? []).some((l) => l.stockLiveAt);

/** One missing / damaged quantity of a GRN, with its decision. */
export interface ShortfallItem {
  key: string;
  variant: string;
  type: number;
  title: string;
  missing: number;
  damaged: number;
  status: number;
  by?: string;
  at?: string;
  stage?: string;
  note?: string;
}

/**
 * Lines of the GRN with a missing / damaged quantity. Older GRNs (saved before decisions
 * existed) have no status: shown, but nothing to decide.
 */
export const shortfallItems = (
  grn?: InwardGrnRecord | null
): ShortfallItem[] => {
  const titleOf = (variant: string) =>
    grn?.receivedLines?.find((l) => l.variant === variant)?.title ??
    grn?.variants?.find((v) => String(v.variant) === variant)?.title ??
    "-";
  return (grn?.inward?.lines ?? [])
    .filter((l) => (Number(l.missing) || 0) + (Number(l.damaged) || 0) > 0)
    .map((l) => ({
      key: `${l.variant}_${l.type}`,
      variant: String(l.variant),
      type: Number(l.type),
      title: titleOf(String(l.variant)),
      missing: Number(l.missing) || 0,
      damaged: Number(l.damaged) || 0,
      status: Number(l.shortfallStatus) || 0,
      by: l.shortfallBy?.name,
      at: l.shortfallAt,
      stage: l.shortfallStage,
      note: l.shortfallNote,
    }));
};

/** Missing / damaged quantities still waiting for a Warehouse or Finance decision. */
export const pendingShortfallCount = (grn?: InwardGrnRecord | null) =>
  shortfallItems(grn).filter((s) => s.status === SHORTFALL_PENDING).length;

/**
 * Every received product is live. A product goes live when Finance has approved it and
 * Warehouse has verified the GRN.
 */
export const isStockLive = (grn: InwardGrnRecord) => !!grn.inward?.stockLiveAt;

/** Received products Finance has not approved yet. */
export const openFinanceLines = (grn: InwardGrnRecord) =>
  (grn.receivedLines ?? []).filter((l) => l.received > 0 && !l.financeApproved);

/** Cost price changes waiting for acknowledgement. */
export const pendingCostChanges = (grn: InwardGrnRecord) =>
  (grn.receivedLines ?? []).filter(
    (l) => l.costChange?.status === COST_CHANGE_PENDING
  );

export const totalReceived = (grn: InwardGrnRecord) =>
  (grn.variants ?? []).reduce(
    (sum, v) =>
      sum +
      (v.quantityTypes ?? []).reduce(
        (s, q) => s + (Number(q.stockCount) || 0),
        0
      ),
    0
  );

/** Warehouse photos per approval (same limit as the API). Finance documents are unlimited. */
export const MAX_WAREHOUSE_PHOTOS = 20;
const IMAGE_EXT = /\.(jpg|jpeg|png|webp|heic|heif)$/i;

/**
 * Same file rules as the API. Warehouse: 1 to 20 photos. Finance: at least one file of any
 * type and size. Returns an error message, or null when the files are fine.
 */
export const checkApprovalFiles = (
  kind: ApprovalKind,
  files: File[]
): string | null => {
  if (files.length === 0) {
    return kind === "warehouse"
      ? "Take at least one photo with the camera as proof of verification"
      : "Add at least one document (take a photo or upload a file)";
  }
  if (kind === "finance") return null;
  if (files.length > MAX_WAREHOUSE_PHOTOS)
    return `You can attach up to ${MAX_WAREHOUSE_PHOTOS} photos`;
  const bad = files.find((f) => !IMAGE_EXT.test(f.name));
  return bad ? `${bad.name}: only photos taken with the camera` : null;
};

/** Multipart body for Warehouse approval: photos in "images". */
export const buildApprovalForm = (
  kind: ApprovalKind,
  files: File[]
): FormData => {
  const form = new FormData();
  const field = kind === "warehouse" ? "images" : "documents";
  files.forEach((file) => form.append(field, file));
  return form;
};

export interface FinanceForm {
  invoiceNumber: string;
  receiptNumber: string;
  receiptVerificationCode: string;
}

export const FINANCE_FIELDS: { key: keyof FinanceForm; label: string }[] = [
  { key: "invoiceNumber", label: "Invoice number" },
  { key: "receiptNumber", label: "Receipt number" },
  { key: "receiptVerificationCode", label: "Receipt verification code" },
];

/** Selected product + optional new cost (text, as typed). */
export interface FinanceLineChoice {
  key: string;
  variant: string;
  type: number;
  /** empty = keep PO cost */
  costPrice: string;
}

/** First problem with the Finance inputs, or null. */
export const checkFinanceForm = (
  fields: FinanceForm,
  lines: FinanceLineChoice[],
  files: File[],
  linesByKey: Map<string, ReceivedLine>
): string | null => {
  const missing = FINANCE_FIELDS.find((f) => !fields[f.key].trim());
  if (missing) return `${missing.label} is required`;
  if (lines.length === 0) return "Select at least one product to approve";
  for (const l of lines) {
    if (l.costPrice.trim() === "") continue;
    const n = Number(l.costPrice);
    if (!Number.isFinite(n) || n <= 0) {
      return `${
        linesByKey.get(l.key)?.title ?? l.variant
      }: cost price must be greater than 0`;
    }
  }
  const noPrice = lines.find(
    (l) => linesByKey.get(l.key)?.newSellingPrice === null
  );
  if (noPrice) {
    return `${
      linesByKey.get(noPrice.key)?.title
    }: no active Price Catalog selling price. Add it in Price Catalog first.`;
  }
  return checkApprovalFiles("finance", files);
};

/** Multipart body for Finance approval. */
export const buildFinanceForm = (
  fields: FinanceForm,
  lines: FinanceLineChoice[],
  files: File[]
): FormData => {
  const form = new FormData();
  FINANCE_FIELDS.forEach((f) => form.append(f.key, fields[f.key].trim()));
  form.append(
    "lines",
    JSON.stringify(
      lines.map((l) => ({
        variant: l.variant,
        type: l.type,
        ...(l.costPrice.trim() !== ""
          ? { costPrice: Number(l.costPrice) }
          : {}),
      }))
    )
  );
  files.forEach((file) => form.append("documents", file));
  return form;
};

export const approvalAction = (kind: ApprovalKind) =>
  kind === "warehouse" ? "warehouse-approval" : "finance-approval";

/** Message after an approval, based on the GRN the API returned. */
export const approvalSuccessMessage = (
  kind: ApprovalKind,
  grn?: InwardGrnRecord | null
): string => {
  const who = kind === "warehouse" ? "Warehouse" : "Finance";
  if (grn?.inward?.stockLiveAt)
    return `${who} approval saved. All received products are live in stock.`;
  const t = grn?.totals;
  const live = t
    ? ` ${t.liveLines} of ${t.lineCount} products live in stock.`
    : "";
  if (kind === "warehouse") {
    return `Warehouse approval saved.${live} The rest go live as Finance approves them.`;
  }
  return isApproved(grn?.inward?.warehouseApproval)
    ? `Finance approval saved.${live}`
    : "Finance approval saved. Stock goes live after Warehouse approval.";
};
